import { FC, useState } from 'react'
import { Autocomplete, InputAdornment, TextField, TextFieldProps } from '@mui/material'
import { TimePicker } from '@mui/x-date-pickers-pro'
import { DateTime } from 'luxon'

import { formatNumber } from '@/common/utils/formatting'
import { getUserLocale } from '@/common/utils/locale'
import { Time } from '@/common/utils/types'

/** An option of a select. The same shape as the old `Dropdown` options. */
export interface SelectOption {
  value?: string
  text: string
}

interface BaseSelectProps {
  id: string
  options: SelectOption[]
  onBlur?: () => void
  placeholder?: string
  error?: boolean
  required?: boolean
  disabled?: boolean
}

interface ThemedSelectProps extends BaseSelectProps {
  value: string | null | undefined
  onChange: (value: string | undefined) => void
}

/**
 * A single select of the policy form. Like the old `Dropdown`, a value can be changed but not
 * cleared.
 */
export const ThemedSelect: FC<ThemedSelectProps> = ({
  id,
  options,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  required,
  disabled,
}) => (
  <Autocomplete
    id={id}
    size="small"
    disableClearable
    disabled={disabled}
    // `undefined` would make the select uncontrolled. `null` (no value) is not allowed with
    // `disableClearable`, so the cast only covers the time before a value is picked.
    value={(options.find(option => option.value === value) ?? null) as SelectOption}
    options={options}
    getOptionLabel={option => option.text}
    getOptionKey={option => option.value ?? option.text}
    isOptionEqualToValue={(option, selected) => option.value === selected.value}
    onChange={(_, option) => onChange(option?.value)}
    onBlur={onBlur}
    renderInput={params => (
      <TextField {...params} placeholder={placeholder} error={error} required={required} />
    )}
  />
)

interface ThemedMultiSelectProps extends BaseSelectProps {
  value: string[]
  onChange: (value: string[]) => void
}

/** A multiple select of the policy form. The values show as chips in the field. */
export const ThemedMultiSelect: FC<ThemedMultiSelectProps> = ({
  id,
  options,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  required,
  disabled,
}) => (
  <Autocomplete
    id={id}
    multiple
    size="small"
    disabled={disabled}
    value={options.filter(option => option.value !== undefined && value.includes(option.value))}
    options={options}
    getOptionLabel={option => option.text}
    getOptionKey={option => option.value ?? option.text}
    isOptionEqualToValue={(option, selected) => option.value === selected.value}
    onChange={(_, selected) =>
      onChange(selected.flatMap(option => (option.value === undefined ? [] : [option.value])))
    }
    onBlur={onBlur}
    renderInput={params => (
      <TextField
        {...params}
        // The placeholder is only useful while nothing is picked.
        placeholder={value.length ? undefined : placeholder}
        error={error}
        // The chips are the value, so the input itself must not be `required`.
        required={required && !value.length}
      />
    )}
  />
)

/** The currency symbol of the region, for example `$` for USD. */
const currencySymbol = (currencyCode = 'USD'): string =>
  new Intl.NumberFormat(getUserLocale().replace(/_/g, '-'), {
    style: 'currency',
    currency: currencyCode,
  })
    .formatToParts(0)
    .find(part => part.type === 'currency')?.value ?? ''

type CurrencyFieldProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
  value: number | null | undefined
  /** The number as the user typed it, without other characters. Empty when the field is empty. */
  onChange: (value: string) => void
  currencyCode?: string
  /** For a field without a visible label, for example in a table cell. */
  ariaLabel?: string
}

/**
 * A price. While the user types, the field shows the text as typed, so a decimal point can be
 * entered. Otherwise it shows the price with two decimals. Like the old `CurrencyInput`, all
 * characters that are not digits or `.` are removed.
 */
export const CurrencyField: FC<CurrencyFieldProps> = ({
  value,
  onChange,
  onBlur,
  currencyCode,
  ariaLabel,
  slotProps,
  ...props
}) => {
  const [draft, setDraft] = useState<string>()
  const hasValue = value !== null && value !== undefined

  return (
    <TextField
      size="small"
      {...props}
      value={draft ?? (hasValue ? formatNumber(value, 2) : '')}
      onFocus={() => setDraft(hasValue ? String(value) : '')}
      onChange={event => {
        const cleaned = event.target.value.replace(/[^0-9.]/g, '')
        setDraft(cleaned)
        onChange(cleaned)
      }}
      onBlur={event => {
        setDraft(undefined)
        onBlur?.(event)
      }}
      slotProps={{
        ...slotProps,
        input: {
          startAdornment: (
            <InputAdornment position="start">{currencySymbol(currencyCode)}</InputAdornment>
          ),
        },
        htmlInput: { inputMode: 'decimal', 'aria-label': ariaLabel },
      }}
    />
  )
}

interface ThemedTimeFieldProps {
  label?: string
  /** For a field without a visible label, for example in a table cell. */
  ariaLabel?: string
  value: Time | null | undefined
  onChange: (value: Time | undefined) => void
  onBlur?: () => void
  /** The policy type only supports whole hours: the minutes are always `00`. */
  hourOnly?: boolean
  error?: boolean
}

/** A time of day in 24-hour format, as the old `TimeInput`. */
export const ThemedTimeField: FC<ThemedTimeFieldProps> = ({
  label,
  ariaLabel,
  value,
  onChange,
  onBlur,
  hourOnly = false,
  error,
}) => (
  <TimePicker
    label={label}
    ampm={false}
    format="HH:mm"
    views={hourOnly ? ['hours'] : ['hours', 'minutes']}
    value={value ? DateTime.fromObject({ hour: value.hour, minute: value.minute }) : null}
    onChange={time => {
      if (!time) return onChange(undefined)
      // A half-typed time is not valid yet. Wait for the rest.
      if (!time.isValid) return
      onChange({ hour: time.hour, minute: hourOnly ? 0 : time.minute })
    }}
    slotProps={{
      textField: {
        size: 'small',
        onBlur,
        error,
        // The field is a `group` of time sections, named by its label. Without a label, name
        // the group itself.
        InputProps: ariaLabel ? { 'aria-label': ariaLabel } : undefined,
      },
    }}
  />
)
